import React, { memo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';
import { HighlightWords, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationActiveIngredientTitle = memo((props: Props) => {
  const { ingredientName, quantity, isSplitPortion, isHalfPortion } = props;

  const { formatMessage } = useIntl();

  const shouldUseQuantity = quantity !== undefined && quantity > 1 && !isSplitPortion; // prettier-ignore
  const quantityText = shouldUseQuantity ? `${quantity}x` : undefined;

  // ─── Half Portions ─────────────────────────────────────────────────────────

  if (isHalfPortion) {
    const text = formatMessage(messages.halfPortionWithName, { name: ingredientName }); // prettier-ignore

    return (
      <View style={styles.container}>
        <HighlightWords
          sizeMatch={['20']}
          text={text}
          highlights={[formatMessage(messages.half)]}
          style={styles.text}
        />
      </View>
    );
  }

  // ─── Standard Portions ─────────────────────────────────────────────────────

  const text = quantityText
    ? formatMessage(messages.nameWithQuantity, { quantity: quantityText, name: ingredientName }) // prettier-ignore
    : ingredientName;

  return (
    <View style={styles.container}>
      <HighlightWords
        sizeMatch={['20']}
        text={text}
        highlights={quantityText ? [quantityText] : []}
        style={styles.text}
      />
    </View>
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  nameWithQuantity: {
    defaultMessage: '{quantity} {name}',
    description:
      'Customization | Active ingredient | Title | The name of the ingredient and its quantity',
  },
  half: {
    defaultMessage: 'Half',
    description:
      'Customization | Active ingredient | Title | Half portion label',
  },
  halfPortionWithName: {
    defaultMessage: 'Half {name}',
    description:
      'Customization | Active ingredient | Title | Half portion with ingredient name',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    marginTop: 2, // to better align vertically
  },
  text: {
    color: theme.colors.KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  ingredientName: string;
  quantity: number | undefined;
  isSplitPortion?: boolean;
  isHalfPortion?: boolean;
};
